import {computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, toRefs, watch} from 'vue';
import EventBus from '../../scripts/lib/EventBus.js';
import Swiper from 'swiper';
import {Navigation, Pagination, A11y} from 'swiper/modules';
import {mediaQuery} from '../../scripts/lib/mediaQuery.js';
import {
  observeAndTrackColorSelectionContainer,
  previewDifferentColor,
  releasePreviewDifferentColor,
  trackColorChange
} from '../../scripts/colorHelper.js';

const templateElement = document.getElementById('color-beauty-slider-template');

export default {
  template: templateElement ? templateElement.innerHTML : '',

  props: {
    colors: {
      type: Array,
      required: true,
    },
    productId: {
      type: Number,
      required: false,
    },
    isLayer: {
      type: Boolean,
      required: false,
      default: false,
    },
    noSwiper: {
      type: Boolean,
      required: false,
      default: false,
    }
  },

  setup(props) {
    const swiper = ref(null);
    const swiperInstance = ref(null);
    const colorSelectionObserver = ref(null);

    const state = reactive({
      colorTracking: null,
      checkIfTouchDevice: mediaQuery.checkIfTouchDevice(),
      thumbnailContainerSeen: false,
    });

    const colorGroups = computed(() => {
      const groups = [];
      for (let i = 0; i < props.colors.length; i += 4) {
        groups.push(props.colors.slice(i, i + 4));
      }

      return groups;
    });

    function selectColor(articleId) {
      releasePreviewDifferentColor();
      EventBus.emit('ents:vue:color:changed', articleId);
      if (this.isLayer) {
        EventBus.emit('ents:vue:layer:hide');
      }

      trackColorChange(this.colorTracking[articleId]);
    }

    function initializeColorSlider() {
      if (props.noSwiper) {
        return;
      }
      let animationSpeedInMS = 300;
      if (mediaQuery.prefersReducedMotion()) {
        /* https://github.com/nolimits4web/Swiper/issues/1702 */
        animationSpeedInMS = 1;
      }
      swiperInstance.value = new Swiper(swiper.value, { // NOSONAR
        modules: [Navigation, Pagination, A11y],
        speed: animationSpeedInMS,
        autoplay: false,
        loop: false,
        initialSlide: getActiveSlide(),
        slidesPerView: 1.5,
        slidesPerGroup: 1,
        a11y: {
          // disable a11y attributes on slides
          slideRole: null,
          slideLabelMessage: null,
        },
        navigation: {
          prevEl: `.ents-colors--slider__button-prev${props.isLayer ? '.ents-colors--modal-slider-button' : '.ents-colors--main-slider-button'}`,
          nextEl: `.ents-colors--slider__button-next${props.isLayer ? '.ents-colors--modal-slider-button' : '.ents-colors--main-slider-button'}`,
        },
        pagination: {
          el: `.ents-colors--slider__pagination${props.isLayer ? '.ents-colors--sub-pagination' : '.ents-colors--main-pagination'}`,
          clickable: true,
        },
        breakpoints: {
          [mediaQuery.breakpoints.S]: {
            slidesPerView: 2.5,
            slidesPerGroup: 2,
          },
          [mediaQuery.breakpoints.L]: {
            slidesPerView: 2,
            slidesPerGroup: 2,
          },
        },
        on: {
          // remove a11y attributes from pagination bullets (automatically added by A11y module)
          paginationRender: (swiper, paginationEl) => {
            nextTick(() => {
              if (!swiper || !paginationEl) {
                return;
              }
              const bullets = paginationEl.children;
              for (let i = 0; i < bullets.length; i++) {
                const bullet = bullets.item(i);
                bullet?.removeAttribute('tabindex');
                bullet?.removeAttribute('role');
                bullet?.removeAttribute('aria-label');
                bullet?.removeAttribute('aria-current');
              }
            });
          },
        },
      });
    }

    function getActiveSlide() {
      const activeColorTile = document.querySelector('.ents-color-tile--active');
      let activeIndex = 0;
      if (activeColorTile) {
        activeIndex = activeColorTile.getAttribute('data-slider-index');
      }
      return parseInt(activeIndex, 10);
    }

    onMounted(() => {
      initializeColorSlider();
      state.colorTracking = document.querySelector('.ents-colors__tracking').dataset.entsColorsTrackingJsonContent;
      initializeColorTrackingObserver();
      window.addEventListener('pageshow', resetTrackingInObserver);
    });

    onBeforeUnmount(() => {
      window.removeEventListener('pageshow', resetTrackingInObserver);
    });

    function resetTrackingInObserver(e) {
      if (e.persisted && colorSelectionObserver.value) {
        colorSelectionObserver.value.stop();
        initializeColorTrackingObserver();
      }
    }

    function initializeColorTrackingObserver() {
      if (!props.isLayer) {
        const containerElement = document.querySelector('.ents-colors__list,.ents-colors--slider__swiper-container');
        colorSelectionObserver.value = observeAndTrackColorSelectionContainer(containerElement, props.colors, props.productId);
      }
    }

    watch(() => props.colors, () => {
      nextTick(() => {
        const activeIndex = getActiveSlide();
        if (swiperInstance.value && swiperInstance.value.clickedIndex !== activeIndex && !props.isLayer) {
          swiperInstance.value.slideTo(activeIndex);
        }
      });
    }, {deep: true});

    return {
      ...toRefs(state),
      colorGroups,
      previewDifferentColor,
      releasePreviewDifferentColor,
      selectColor,
      swiper,
    };
  }
};
